import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Form,
  InputGroup,
  Dropdown,
  Badge,
  Card,
  Row,
  Col,
} from "react-bootstrap";
import { ThreeDotsVertical, Search } from "react-bootstrap-icons";
import "./company.css";
import CreateCompanyModal from "components/Modals/CreateCompanyModal";
import { useNavigate } from "react-router-dom";
import { CompanyService } from "Services/CompanyService";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faKey,
  faPen,
  faToggleOff,
  faToggleOn,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import DeleteCompanyModal from "components/Modals/DeleteCompanyModal";
import TablePagination from "components/Pagination/Table.paginaition";
import ResetPasswordModal from "components/Modals/ResetPasswordModal";
import ResetCompanyPasswordModal from "components/Modals/ResetCompanyPasswordModal";

interface CompanyType {
  id: string;
  company_code: string;
  name: string;
  email: string;
  phone: string;
  status: "ACTIVE" | "INACTIVE";
  createdAt: string;
}

const Company = () => {
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [companies, setCompanies] = useState<CompanyType[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [showResetModal, setShowResetModal] = useState(false);
  const navigate = useNavigate();

  const filteredCompanies = companies.filter((company) =>
    company.name.toLowerCase().includes(search.toLowerCase()),
  );

  const getCompany = async () => {
    try {
      const res = await CompanyService.getCompany(currentPage, perPage, search);

      if (res?.status === 200) {
        const list = res?.data?.data?.data || [];
        const totalCount = res.data?.data?.total || 0;

        const formattedData: CompanyType[] = list.map((item: any) => ({
          id: item._id,
          company_code: item.company_code,
          name: item.name,
          email: item.email,
          phone: item.phone,
          status:
            item.status?.toUpperCase() === "ACTIVE" ? "ACTIVE" : "INACTIVE",
          createdAt: new Date(item.createdAt).toLocaleDateString(),
        }));

        setCompanies(formattedData);
        setTotal(totalCount);
      }
    } catch (error) {
      toast.error("Unable to fetch company data");
    }
  };

  useEffect(() => {
    getCompany();
  }, [currentPage, perPage, search]);

  const updateStatus = async (com: CompanyType) => {
    try {
      const newStatusUI = com.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

      const newStatusBackend = com.status === "ACTIVE" ? "suspended" : "active";

      const res = await CompanyService.updateCompanyStatus(com.id, {
        status: newStatusBackend,
      });

      if (res?.status === 200) {
        toast.success("Status updated successfully");

        setCompanies((prev) =>
          prev.map((i) =>
            i.id === com.id ? { ...i, status: newStatusUI } : i,
          ),
        );
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to update status");
    }
  };

  const confirmDeleteCompany = async () => {
    if (!selectedCompany) return;

    try {
      const res = await CompanyService.deletecompany(selectedCompany.id);

      if (res?.status === 200) {
        toast.success("Company  deleted successfully");

        setCompanies((prev) => prev.filter((i) => i.id !== selectedCompany.id));

        setShowDeleteModal(false);
        setSelectedCompany(null);
      }
    } catch (err) {
      toast.error("Failed to delete company");
    }
  };

  const handleMakeBucketPublic = async (id: string) => {
    try {
      await CompanyService.makeBucketPublic(id);
      toast.success("Bucket made public successfully");
      getCompany();
    } catch {
      toast.error("Failed to make bucket public");
    }
  };

  const handleMakeBucketPrivate = async (id: string) => {
    try {
      await CompanyService.makeBucketPrivate(id);
      toast.success("Bucket made private successfully");
      getCompany();
    } catch {
      toast.error("Failed to make bucket private");
    }
  };

  return (
    <div className="company-page">
      <Card className="company-card shadow-lg border-0">
        <Card.Body>
          <Row className="mb-4 align-items-center">
            <Col md={4}>
              <h4 className="fw-bold mb-0">Company Management</h4>
              <small className="text-muted">
                Manage all registered companies
              </small>
            </Col>

            <Col md={4}>
              <InputGroup>
                <InputGroup.Text className="bg-white">
                  <Search />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search company..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </InputGroup>
            </Col>

            <Col md={4} className="text-end">
              <Button className="create-btn" onClick={() => setShowModal(true)}>
                Create Company
              </Button>
            </Col>
          </Row>

          <div className="table-responsive">
            <Table hover className="align-middle custom-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Company Code</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredCompanies.map((company, index) => (
                  <tr key={company.id}>
                    <td>{index + 1}</td>
                    <td>{company.company_code}</td>
                    <td className="fw-semibold">{company.name}</td>
                    <td>{company.email}</td>
                    <td>{company.phone}</td>
                    <td>
                      <Badge
                        bg={company.status === "ACTIVE" ? "success" : "danger"}
                        className="px-3 py-2"
                      >
                        {company.status}
                      </Badge>
                    </td>
                    <td>{company.createdAt}</td>
                    <td className="text-center">
                      <Dropdown align="end">
                        <Dropdown.Toggle variant="light" className="action-btn">
                          <ThreeDotsVertical />
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() =>
                              navigate(`/admin-dash/company/${company.id}`)
                            }
                          >
                            <FontAwesomeIcon
                              icon={faEye}
                              className="me-2 text-secondary"
                            />
                            View Company Details
                          </Dropdown.Item>

                          <Dropdown.Item
                            onClick={() =>
                              navigate(`/admin-dash/company/edit/${company.id}`)
                            }
                          >
                            <FontAwesomeIcon
                              icon={faPen}
                              className="me-2 text-primary"
                            />
                            Edit Company
                          </Dropdown.Item>

                          <Dropdown.Item onClick={() => updateStatus(company)}>
                            <FontAwesomeIcon
                              icon={
                                company.status === "ACTIVE"
                                  ? faToggleOff
                                  : faToggleOn
                              }
                              className="me-2 text-warning"
                            />
                            Set{" "}
                            {company.status === "ACTIVE"
                              ? "Inactive"
                              : "Active"}
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => handleMakeBucketPublic(company.id)}
                          >
                            🌐 Make Bucket Public
                          </Dropdown.Item>

                          <Dropdown.Item
                            onClick={() => handleMakeBucketPrivate(company.id)}
                          >
                            🔒 Make Bucket Private
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => {
                              setSelectedCompany(company);
                              setShowResetModal(true);
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faKey}
                              className="me-2 text-warning"
                            />
                            Reset Password
                          </Dropdown.Item>

                          <Dropdown.Divider />

                          <Dropdown.Item
                            className="text-danger"
                            onClick={() => {
                              setSelectedCompany(company);
                              setShowDeleteModal(true);
                            }}
                          >
                            <FontAwesomeIcon icon={faTrash} className="me-2" />
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}

                {filteredCompanies.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-4">
                      No companies found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
        <TablePagination
          total={total}
          currentPage={currentPage}
          perPage={perPage}
          handlePageChange={(page: number) => setCurrentPage(page)}
          setPerPage={(size: number) => {
            setPerPage(size);
            setCurrentPage(1);
          }}
        />
      </Card>

      <CreateCompanyModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        onSuccess={getCompany}
      />

      <ResetCompanyPasswordModal
        show={showResetModal}
        company={selectedCompany}
        onClose={() => setShowResetModal(false)}
      />
      <DeleteCompanyModal
        show={showDeleteModal}
        company={selectedCompany}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDeleteCompany}
      />
    </div>
  );
};

export default Company;
